/** @jsx isolateComponent */
import { useState } from 'react';
import { css } from '@emotion/core';

import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';

import { createUniqueCustomProperties } from '../../../utils/createUniqueCustomProperty';
import { Animation, Colors, Shadows, Typography } from '../../../theme';
import { Icon, IconName } from '../../display/Icon';
import { Link } from '../../navigation/Link';

type CssValue = string | number;
type ButtonColorTheme = {
  base?: { [cssProperty: string]: CssValue };
  disabled?: { [cssProperty: string]: CssValue };
  hover?: { [cssProperty: string]: CssValue };
  focus?: { [cssProperty: string]: CssValue };
  selected?: { [cssProperty: string]: CssValue };
};

type ButtonColorThemes = {
  [color: string]: ButtonColorTheme;
};

/**
 * @deprecated in favor of using CSS Custom Properties.
 * BREAKING_TODO: Remove.
 */
export const DefaultButtonColors: ButtonColorThemes = {
  // Primary - Blue
  blue: {
    base: {
      backgroundColor: Colors.blue500,
      color: Colors.white,
    },
    disabled: {
      backgroundColor: Colors.grey100,
      color: Colors.grey500,
    },
    hover: {
      backgroundColor: Colors.blue700,
      color: Colors.white,
    },
    focus: {
      backgroundColor: Colors.blue500,
      outlineColor: Colors.blue500,
      color: Colors.white,
    },
  },
  // Secondary - Black
  black: {
    base: {
      backgroundColor: Colors.black,
      color: Colors.white,
    },
    disabled: {
      backgroundColor: Colors.grey100,
      color: Colors.grey500,
    },
    hover: {
      backgroundColor: Colors.grey900,
      color: Colors.white,
    },
    focus: {
      backgroundColor: Colors.black,
      outlineColor: Colors.grey700,
      color: Colors.white,
    },
  },
  // Secondary - Grey
  grey: {
    base: {
      backgroundColor: Colors.grey100,
      color: Colors.blue500,
    },
    disabled: {
      backgroundColor: Colors.grey100,
      color: Colors.grey500,
    },
    hover: {
      backgroundColor: Colors.blue500,
      color: Colors.white,
    },
    focus: {
      backgroundColor: Colors.blue500,
      outlineColor: Colors.blue500,
      color: Colors.white,
    },
  },
  // Tertiary - Grey
  greyAlt: {
    base: {
      backgroundColor: Colors.grey100,
      color: Colors.black,
    },
    disabled: {
      backgroundColor: Colors.grey100,
      color: Colors.grey500,
    },
    hover: {
      backgroundColor: Colors.black,
      color: Colors.white,
    },
    focus: {
      backgroundColor: Colors.black,
      outlineColor: Colors.black,
      color: Colors.white,
    },
    selected: {
      backgroundColor: Colors.black,
      color: Colors.white,
    },
  },
  orange: {
    base: {
      backgroundColor: Colors.red500,
      color: Colors.white,
    },
    disabled: {
      backgroundColor: Colors.grey100,
      color: Colors.grey500,
    },
    hover: {
      backgroundColor: Colors.red600,
      color: Colors.white,
    },
    focus: {
      backgroundColor: Colors.red500,
      outlineColor: Colors.red500,
      color: Colors.white,
    },
  },
  // Tertiary - Stroke
  white: {
    base: {
      backgroundColor: Colors.white,
      color: Colors.black,
      borderColor: Colors.grey400,
    },
    disabled: {
      backgroundColor: Colors.grey100,
      borderColor: Colors.transparent,
      color: Colors.grey500,
    },
    hover: {
      backgroundColor: Colors.white,
      color: Colors.blue500,
      borderColor: Colors.grey400,
    },
    focus: {
      backgroundColor: Colors.white,
      borderColor: Colors.blue500,
      outlineColor: Colors.blue500,
      color: Colors.blue500,
    },
  },
};

type DerivedState = {
  isFocused: boolean;
  isHovered: boolean;
  ButtonContent: React.FC<any>;
  ButtonText: React.FC<any>;
  ButtonIcon: React.FC<any>;
};

export type ButtonColor = keyof typeof DefaultButtonColors;

export type Props = {
  /** Text label for the button or any custom Node, including functions. */
  children: React.ReactNode | ((state: DerivedState) => React.ReactNode);
  /** Changes button to a link. */
  href?: string;
  /**
   * @deprecated - Will be removed in next major release.
   * Change text to upper-case.
   */
  casing?: 'uppercase';
  /** Disable the button. */
  disabled?: boolean;
  /** Should the left, right or all sides be rounded? */
  borderRadiusSide?: 'left' | 'right';
  /** Callback to be executed when the button is clicked. */
  onClick?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  /** Link target */
  target?: '_self' | '_blank' | '_parent' | '_top';
  /** Link rel */
  rel?: string;
  /** DOM type of button tag. */
  type?: 'button' | 'submit' | 'reset';
  /**
   * Name of the color for the button, typically its background.
   * Currently `string` is used as a loose type but that will be
   * eliminated as a BREAKING_TODO.
   */
  color?: string | 'primary' | 'primary-alt' | 'secondary' | 'tertiary';
  /**
   * Color themes for the button. The default value for this prop
   * should only be overridden in rare cases, and `color` should be
   * leveraged as much as possible instead.
   */
  colorTheme?: ButtonColorThemes;
  /**
   * Optional `for` attribute to apply to node.
   * If supplied, the rendered tag will be a `label`.
   */
  htmlFor?: string;
  /** Optional icon to render before `children` */
  iconBefore?: IconName;
  /** Optional icon to render after `children` */
  iconAfter?: IconName;
  /** Indicates whether the button content is padded or not. */
  isPadded?: boolean;
  /**
   * @deprecated - Will be removed in next major release.
   * Renders the button in a pill shaped format.
   */
  isPill?: boolean;
  /** Defines whether the button is selected. */
  isSelected?: boolean;
  /** Renders the button in a square, keeping height and width consistent. */
  isSquare?: boolean;
  /** Renders a drop-shadow behind the button */
  hasShadow?: boolean;
  /** Force button to take up full width of container. */
  isFullWidth?: boolean;
  /** The vertical size of the button */
  size?: 'xsmall' | 'small' | 'medium' | 'large';
  /** The id of the element that describes the button, used for aria-describedby*/
  ariaDescribedBy?: string;
  /** An optional aria label to describe the button */
  ariaLabel?: string;
  /** An optional aria-expanded attribute to describe if the button's expandable/collapsible content is expanded currently */
  ariaExpanded?: boolean;
  /** Optionally pass an ID to identify the what element the button controls (e.g. expands/collapses) */
  ariaControls?: string;
  /** An optional aria-haspopup attribute if this button triggers a popup/dropdown */
  ariaHaspopup?: boolean;
  /** An optional aria-hidden attribute if this button should not be available to screenreaders. Only use this to improve functionality for screenreaders, never to remove functionality! */
  ariaHidden?: boolean;
  /** Optionally pass an ID to identify the button */
  id?: string;
  /** When `true`, the button is rendered as a `<span>` rather than as a link or button. */
  presentationOnly?: boolean;
  onKeyDown?: (e: React.KeyboardEvent<HTMLSpanElement>) => void;
  /** On rare occasion tabindex needs to be overridden. Use this sparingly. */
  tabIndex?: number;
};

type ButtonIconProps = {
  iconName: IconName;
  position: 'before' | 'after';
  size: number;
};

function getTag(props: Props) {
  if (props.presentationOnly) {
    return 'span';
  }
  if (props.htmlFor && !props.disabled) {
    return 'label';
  }
  if (props.href && !props.disabled) {
    return Link;
  }
  return 'button';
}

const Vars = createUniqueCustomProperties('Button', [
  'baseBackgroundColor',
  'baseBorderColor',
  'baseColor',
  'disabledBackgroundColor',
  'disabledBorderColor',
  'disabledColor',
  'disabledOutlineColor',
  'hoverBackgroundColor',
  'hoverBorderColor',
  'hoverColor',
  'hoverOutlineColor',
  'focusBackgroundColor',
  'focusBorderColor',
  'focusColor',
  'focusOutlineColor',
  'selectedBackgroundColor',
  'selectedBorderColor',
  'selectedColor',
  'selectedOutlineColor',
  'buttonContentPadding',
]);

const Styles = labelStyles('Button', {
  root: css`
    // All of the CSS Custom Properties that are used by this
    // component, ensured to be unique. Most are color related
    // and are set below via '[data-color]' selectors.
    ${Vars.baseBackgroundColor}: transparent;
    ${Vars.baseBorderColor}: transparent;
    ${Vars.baseColor}: #000;
    ${Vars.disabledBackgroundColor}: ${Colors.gray10};
    ${Vars.disabledBorderColor}: transparent;
    ${Vars.disabledColor}: ${Colors.gray60};
    ${Vars.disabledOutlineColor}: transparent;
    ${Vars.hoverBackgroundColor}: var(${Vars.baseBackgroundColor});
    ${Vars.hoverBorderColor}: var(${Vars.baseBorderColor});
    ${Vars.hoverColor}: var(${Vars.baseColor});
    ${Vars.hoverOutlineColor}: transparent;
    ${Vars.focusBackgroundColor}: var(${Vars.baseBackgroundColor});
    ${Vars.focusBorderColor}: transparent;
    ${Vars.focusColor}: var(${Vars.baseColor});
    ${Vars.focusOutlineColor}: var(${Vars.focusBackgroundColor});
    ${Vars.selectedBackgroundColor}: var(${Vars.baseBackgroundColor});
    ${Vars.selectedBorderColor}: transparent;
    ${Vars.selectedColor}: var(${Vars.baseColor});
    ${Vars.selectedOutlineColor}: transparent;
    ${Vars.buttonContentPadding}: 0 10px;

    background-color: var(${Vars.baseBackgroundColor});
    border-color: var(${Vars.baseBorderColor});
    color: var(${Vars.baseColor});

    align-items: center;
    appearance: none;
    border-radius: 10px;
    border-style: solid;
    border-width: 1px;
    box-sizing: border-box;
    position: relative;
    cursor: pointer;
    display: inline-flex;
    justify-content: center;
    // Safari applies 2px margin to buttons this clears it
    margin: 0;
    min-width: 0;
    opacity: 1;
    text-align: center;
    text-decoration: none;
    text-transform: none;
    transition: all 200ms ease-in-out;
    vertical-align: middle;
    white-space: nowrap;

    &::-moz-focus-inner {
      padding: 0;
      border: 0;
    }

    // This is the outline that renders around the button when
    // its focused. Its a pseudo element to preserve transparenc;
    // between the edge of the button and the edge of the outline.
    // Its defined here vs only in ':focus' so that it can transition
    // between states.
    &::before {
      content: '';
      position: absolute;
      pointer-events: none;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      border: 2px solid transparent;
      border-radius: 12px;
      transition-property: all;
      transition-timing-function: ${Animation.transitionTimingFunction};
      transition-duration: 200ms;
    }

    &:hover:not([disabled]),
    a:hover & {
      background-color: var(${Vars.hoverBackgroundColor});
      border-color: var(${Vars.hoverBorderColor});
      color: var(${Vars.hoverColor});
    }

    &:focus:not([disabled]),
    a:focus & {
      background-color: var(${Vars.focusBackgroundColor});
      border-color: var(${Vars.focusBorderColor});
      color: var(${Vars.focusColor});
      &::before {
        border-color: var(${Vars.focusOutlineColor});
        top: -5px;
        right: -5px;
        bottom: -5px;
        left: -5px;
      }
    }

    &[data-selected] {
      background-color: var(${Vars.selectedBackgroundColor});
      border-color: var(${Vars.selectedBorderColor});
      color: var(${Vars.selectedColor});
      // Hide :focus outline on selected buttons
      &::before {
        content: none;
      }
    }

    &[data-color='blue'] {
      ${Vars.baseBackgroundColor}: ${Colors.blue500};
      ${Vars.baseColor}: ${Colors.white};
      ${Vars.hoverBackgroundColor}: ${Colors.blue700};
    }

    &[data-color='black'] {
      ${Vars.baseBackgroundColor}: ${Colors.black};
      ${Vars.baseColor}: ${Colors.white};
      ${Vars.hoverBackgroundColor}: ${Colors.grey900};
    }

    &[data-color='grey'] {
      ${Vars.baseBackgroundColor}: ${Colors.grey100};
      ${Vars.baseColor}: ${Colors.blue500};
      ${Vars.hoverBackgroundColor}: ${Colors.blue500};
      ${Vars.hoverColor}: ${Colors.white};
      ${Vars.focusBackgroundColor}: ${Colors.blue500};
      ${Vars.focusColor}: ${Colors.white};
    }

    &[data-color='greyAlt'] {
      ${Vars.baseBackgroundColor}: ${Colors.grey100};
      ${Vars.baseColor}: ${Colors.black};
      ${Vars.hoverBackgroundColor}: ${Colors.black};
      ${Vars.hoverColor}: ${Colors.white};
      ${Vars.focusBackgroundColor}: ${Colors.black};
      ${Vars.focusColor}: ${Colors.white};
      ${Vars.selectedBackgroundColor}: ${Colors.black};
      ${Vars.selectedColor}: ${Colors.white};
    }

    &[data-color='orange'] {
      ${Vars.baseBackgroundColor}: ${Colors.red500};
      ${Vars.baseColor}: ${Colors.white};
      ${Vars.hoverBackgroundColor}: ${Colors.red600};
    }

    &[data-color='white'] {
      ${Vars.baseBackgroundColor}: ${Colors.white};
      ${Vars.baseColor}: ${Colors.black};
      ${Vars.baseBorderColor}: ${Colors.grey400};
      ${Vars.hoverColor}: ${Colors.blue500};
      ${Vars.focusBorderColor}: ${Colors.blue500};
      ${Vars.focusOutlineColor}: ${Colors.blue500};
      ${Vars.focusColor}: ${Colors.blue500};
    }

    &[data-color='primary'] {
      ${Vars.baseBackgroundColor}: ${Colors.ocean};
      ${Vars.baseColor}: ${Colors.white};
      ${Vars.hoverBackgroundColor}: ${Colors.ocean120};
    }

    &[data-color='primary-alt'] {
      ${Vars.baseBackgroundColor}: ${Colors.gray};
      ${Vars.baseColor}: ${Colors.white};
      ${Vars.hoverBackgroundColor}: ${Colors.gray80};
    }

    &[data-color='secondary'] {
      ${Vars.baseBackgroundColor}: ${Colors.gray10};
      ${Vars.baseColor}: ${Colors.gray};
      ${Vars.hoverBackgroundColor}: ${Colors.ocean};
      ${Vars.hoverColor}: ${Colors.white};
      ${Vars.focusBackgroundColor}: ${Colors.ocean};
      ${Vars.focusColor}: ${Colors.white};
    }

    &[data-color='tertiary'] {
      ${Vars.baseBackgroundColor}: transparent;
      ${Vars.baseColor}: ${Colors.ocean};
      ${Vars.hoverColor}: ${Colors.ocean120};
      ${Vars.focusOutlineColor}: ${Colors.ocean};
      ${Vars.focusColor}: ${Colors.ocean};

      &[disabled] {
        background-color: transparent;
      }
    }

    &[data-color='icon-primary'] {
      ${Vars.baseBackgroundColor}: ${Colors.gray10};
      ${Vars.baseColor}: ${Colors.gray};
      ${Vars.hoverColor}: ${Colors.ocean};
      ${Vars.focusOutlineColor}: ${Colors.ocean};
      ${Vars.focusColor}: ${Colors.ocean};
    }

    &[data-color='icon-alt'] {
      ${Vars.baseBackgroundColor}: transparent;
      ${Vars.baseColor}: ${Colors.ocean};
      ${Vars.hoverColor}: ${Colors.ocean120};
      ${Vars.focusOutlineColor}: ${Colors.ocean};
      ${Vars.focusColor}: ${Colors.ocean};

      &[disabled] {
        background-color: transparent;
      }
    }

    &[disabled] {
      cursor: not-allowed;
      background-color: var(${Vars.disabledBackgroundColor});
      border-color: var(${Vars.disabledBorderColor});
      color: var(${Vars.disabledColor});
      outline-color: var(${Vars.disabledOutlineColor});
    }

    &[data-casing='uppercase'] {
      text-transform: uppercase;
    }

    &[data-full-width] {
      width: 100%;
    }

    &[data-pill] {
      border-radius: 100px;
      &::before {
        border-radius: inherit;
      }
    }

    &[data-shadowed] {
      box-shadow: ${Shadows.elevation5};
    }

    &[data-size='xsmall'] {
      ${Typography.smallPrint1Bold};
      ${Vars.buttonContentPadding}: 0 20px;
      height: 30px;
      border-radius: 7px;

      &[data-square] {
        width: 30px;
      }
    }

    &[data-size='small'] {
      ${Typography.smallPrint1Bold};
      ${Vars.buttonContentPadding}: 0 20px;
      height: 40px;

      &[data-square] {
        width: 40px;
      }
    }

    &[data-size='medium'] {
      ${Typography.paragraph3Bold}
      ${Vars.buttonContentPadding}: 0 30px;
      height: 50px;

      &[data-square] {
        width: 50px;
      }
    }

    &[data-size='large'] {
      ${Typography.paragraph3Bold}
      ${Vars.buttonContentPadding}: 0 30px;
      height: 60px;

      &[data-square] {
        width: 60px;
      }
    }

    &[data-unpadded],
    &[data-square] {
      ${Vars.buttonContentPadding}: 0;
    }

    &[data-border-radius-side='left'] {
      border-top-right-radius: 0px;
      border-bottom-right-radius: 0px;

      &::before {
        border-top-right-radius: inherit;
        border-bottom-right-radius: inherit;
      }
    }

    &[data-border-radius-side='right'] {
      border-top-left-radius: 0px;
      border-bottom-left-radius: 0px;

      &::before {
        border-top-left-radius: inherit;
        border-bottom-left-radius: inherit;
      }
    }
  `,
  buttonContent: css`
    display: flex;
    align-items: center;
    min-width: 0;
    padding: var(${Vars.buttonContentPadding});
  `,
  buttonText: css`
    display: inline-block;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
  `,
  buttonIcon: css`
    display: block;
    flex: none;

    &[data-position='before'] {
      margin-right: 6px;
    }

    &[data-position='after'] {
      margin-left: 6px;
    }
  `,
});

const ButtonIcon = (props: ButtonIconProps) => (
  <span css={Styles.buttonIcon} data-position={props.position}>
    <Icon
      canAcceptPointerEvents={false}
      isBlock={true}
      name={props.iconName}
      size={props.size}
    />
  </span>
);

const ButtonText: React.FC = (props) => (
  <span css={Styles.buttonText}>{props.children}</span>
);

function ButtonContent(props: Props) {
  return (
    // @ts-ignore props doesn't take into account optional props
    <span css={Styles.buttonContent}>
      {props.iconBefore && (
        <ButtonIcon iconName={props.iconBefore} position="before" size={24} />
      )}
      <ButtonText>{props.children}</ButtonText>
      {props.iconAfter && (
        <ButtonIcon iconName={props.iconAfter} position="after" size={24} />
      )}
    </span>
  );
}

/**
 * Classic button component. Use when providing calls to action to users.
 *
 * 🚨 There are currently some issues rendering props in the styleguide.
 * In the meantime, please [reference the `Props` definition in GitHub](https://github.com/zapier/design-system/blob/master/packages/design-system/src/components/navigation/Button/Button.tsx#L169).
 */
export function Button(props: Props) {
  const ButtonTag = getTag(props);
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const {
    children,
    href,
    htmlFor,
    id,
    ariaLabel,
    target,
    rel,
    type,
    disabled,
    ariaDescribedBy,
    onClick,
    presentationOnly,
    onKeyDown,
    ariaExpanded,
    ariaControls,
    ariaHaspopup,
    ariaHidden,
    tabIndex,
  } = props;

  const role = disabled && href ? 'button' : undefined;
  const derivedState = {
    isFocused: !disabled && isFocused,
    isHovered: !disabled && isHovered,
  };

  // These properties are only applied if the rendered element is a `<button>`.
  const Props = !presentationOnly
    ? {
        type,
      }
    : {};

  // These properties are only applied if the rendered element is an `<a>`.
  const linkProps =
    href && !disabled && !presentationOnly
      ? {
          ariaLabel,
          href,
          rel,
          target,
        }
      : {};

  const theme = props.colorTheme?.[props.color || ''];
  const style =
    theme && props.colorTheme !== Button.defaultProps.colorTheme
      ? {
          [Vars.baseBackgroundColor]: theme.base?.backgroundColor,
          [Vars.baseBorderColor]: theme.base?.borderColor,
          [Vars.baseColor]: theme.base?.color,
          [Vars.disabledBackgroundColor]: theme.disabled?.backgroundColor,
          [Vars.disabledBorderColor]: theme.disabled?.borderColor,
          [Vars.disabledColor]: theme.disabled?.color,
          [Vars.disabledOutlineColor]: theme.disabled?.outlineColor,
          [Vars.hoverBackgroundColor]: theme.hover?.backgroundColor,
          [Vars.hoverBorderColor]: theme.hover?.borderColor,
          [Vars.hoverColor]: theme.hover?.color,
          [Vars.hoverOutlineColor]: theme.hover?.outlineColor,
          [Vars.focusBackgroundColor]: theme.focus?.backgroundColor,
          [Vars.focusBorderColor]: theme.focus?.borderColor,
          [Vars.focusColor]: theme.focus?.color,
          [Vars.focusOutlineColor]: theme.focus?.outlineColor,
          [Vars.selectedBackgroundColor]: theme.selected?.backgroundColor,
          [Vars.selectedBorderColor]: theme.selected?.borderColor,
          [Vars.selectedColor]: theme.selected?.color,
          [Vars.selectedOutlineColor]: theme.selected?.outlineColor,
        }
      : {};

  return (
    // @ts-ignore href apparently can't be undefined
    <ButtonTag
      aria-controls={ariaControls}
      aria-describedby={ariaDescribedBy}
      aria-disabled={disabled}
      aria-expanded={ariaExpanded}
      aria-haspopup={ariaHaspopup}
      aria-hidden={ariaHidden}
      aria-label={ariaLabel}
      // @ts-ignore doesn't understand Link[color=null]
      color={ButtonTag === Link ? null : undefined}
      // @ts-ignore props has href possibly undefined
      css={Styles.root}
      data-color={props.color}
      data-selected={props.isSelected ? '' : undefined}
      data-casing={props.casing}
      data-full-width={props.isFullWidth ? '' : undefined}
      data-unpadded={!props.isPadded ? '' : undefined}
      data-pill={props.isPill ? '' : undefined}
      data-shadowed={props.hasShadow ? '' : undefined}
      data-size={props.size}
      data-square={props.isSquare ? '' : undefined}
      data-border-radius-side={props.borderRadiusSide}
      disabled={disabled}
      htmlFor={htmlFor}
      id={id}
      onBlur={() => setIsFocused(false)}
      onClick={onClick}
      onFocus={() => setIsFocused(true)}
      onKeyDown={onKeyDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role={role}
      style={style}
      tabIndex={tabIndex}
      {...Props}
      {...linkProps}
    >
      {typeof children === 'function' ? (
        (children as (state: DerivedState) => React.ReactNode)({
          ...derivedState,
          ButtonContent,
          ButtonText,
          ButtonIcon,
        })
      ) : (
        <ButtonContent {...props} />
      )}
    </ButtonTag>
  );
}

Button.defaultProps = {
  color: 'blue',
  colorTheme: DefaultButtonColors,
  disabled: false,
  isFullWidth: false,
  isPadded: true,
  isPill: false,
  size: 'medium',
};
